import React, { useState, useEffect } from 'react';
import { placementAPI, apiUtils } from '../../utils/api';
import { toast } from 'react-toastify';
import PlacementTable from './PlacementTable';
import PlacementForm from './PlacementForm';
import Analytics from './Analytics';
import TimelineView from './TimelineView';
import ScheduleView from './ScheduleView';
import './PlacementDashboard.css';

const PlacementDashboard = () => {
  const [activeTab, setActiveTab] = useState('table');
  const [placements, setPlacements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPlacement, setSelectedPlacement] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [filters, setFilters] = useState({
    page: 1,
    limit: 10,
    status: '',
    company: '',
    season: '',
    dateFrom: '',
    dateTo: '',
    priority: '',
    sortBy: 'created_at',
    sortOrder: 'desc',
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 0,
  });

  // Load placements on component mount and filter changes
  useEffect(() => {
    loadPlacements();
  }, [filters]);

  const loadPlacements = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await placementAPI.getPlacements(filters);
      
      setPlacements(response.placements);
      setPagination(response.pagination);
    } catch (error) {
      const errorInfo = apiUtils.handleError(error);
      setError(errorInfo.message);
      toast.error(`Failed to load placements: ${errorInfo.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePlacement = async (placementData) => {
    try {
      const response = await placementAPI.createPlacement(placementData);
      toast.success('Placement created successfully!');
      setShowForm(false);
      loadPlacements(); // Reload data
      return { success: true };
    } catch (error) {
      const errorInfo = apiUtils.handleError(error);
      toast.error(`Failed to create placement: ${errorInfo.message}`);
      return { success: false, error: errorInfo.message };
    }
  };

  const handleUpdatePlacement = async (id, placementData) => {
    try {
      const response = await placementAPI.updatePlacement(id, placementData);
      toast.success('Placement updated successfully!');
      setSelectedPlacement(null);
      setShowForm(false);
      loadPlacements(); // Reload data
      return { success: true };
    } catch (error) {
      const errorInfo = apiUtils.handleError(error);
      toast.error(`Failed to update placement: ${errorInfo.message}`);
      return { success: false, error: errorInfo.message };
    }
  };

  const handleDeletePlacement = async (id) => {
    if (!window.confirm('Are you sure you want to delete this placement?')) {
      return;
    }

    try {
      await placementAPI.deletePlacement(id);
      toast.success('Placement deleted successfully!');
      loadPlacements(); // Reload data
    } catch (error) {
      const errorInfo = apiUtils.handleError(error);
      toast.error(`Failed to delete placement: ${errorInfo.message}`);
    }
  };

  const handleBulkDelete = async (ids) => {
    if (!window.confirm(`Are you sure you want to delete ${ids.length} placement(s)?`)) {
      return;
    }

    try {
      await placementAPI.bulkDeletePlacements(ids);
      toast.success(`${ids.length} placement(s) deleted successfully!`);
      loadPlacements(); // Reload data
    } catch (error) {
      const errorInfo = apiUtils.handleError(error);
      toast.error(`Failed to delete placements: ${errorInfo.message}`);
    }
  };

  const handleExport = async (format = 'csv') => {
    try {
      await placementAPI.exportPlacements(format, filters.season);
      toast.success(`Data exported successfully as ${format.toUpperCase()}!`);
    } catch (error) {
      const errorInfo = apiUtils.handleError(error);
      toast.error(`Failed to export data: ${errorInfo.message}`);
    }
  };

  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters,
      page: 1, // Reset to first page when filters change
    }));
  };

  const handlePageChange = (page) => {
    setFilters(prev => ({
      ...prev,
      page,
    }));
  };

  const handleEdit = (placement) => {
    setSelectedPlacement(placement);
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setSelectedPlacement(null);
    setShowForm(false);
  };

  const handleGoHome = () => {
    window.location.href = '/';
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'table':
        return (
          <PlacementTable
            placements={placements}
            loading={loading}
            error={error}
            filters={filters}
            pagination={pagination}
            onFilterChange={handleFilterChange}
            onPageChange={handlePageChange}
            onEdit={handleEdit}
            onDelete={handleDeletePlacement}
            onBulkDelete={handleBulkDelete}
            onExport={handleExport}
            onRefresh={loadPlacements}
          />
        );
      
      case 'analytics':
        return <Analytics filters={filters} />;
      
      case 'timeline':
        return <TimelineView placements={placements} filters={filters} />;
      
      case 'schedule':
        return <ScheduleView placements={placements} filters={filters} />;
      
      default:
        return null;
    }
  };

  return (
    <div className="placement-dashboard">
      {/* Header */}
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <h1 className="dashboard-title">
              <i className="fas fa-briefcase me-2"></i>
              Placement Dashboard
            </h1>
            <p className="dashboard-subtitle">
              Track and manage your job applications
            </p>
          </div>
          
          <div className="header-right">
            <div className="user-info">
              <div className="user-actions">
                <button
                  className="btn btn-outline-primary btn-sm me-2"
                  onClick={() => setShowForm(true)}
                >
                  <i className="fas fa-plus me-1"></i>
                  Add Placement
                </button>
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={handleGoHome}
                >
                  <i className="fas fa-home me-1"></i>
                  Home
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Tabs */}
      <nav className="dashboard-nav">
        <div className="nav-tabs">
          {[
            { id: 'table', label: 'Data Table', icon: 'fa-table' },
            { id: 'analytics', label: 'Analytics', icon: 'fa-chart-bar' },
            { id: 'timeline', label: 'Timeline', icon: 'fa-timeline' },
            { id: 'schedule', label: 'Schedule', icon: 'fa-calendar' },
          ].map(tab => (
            <button
              key={tab.id}
              className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <i className={`fas ${tab.icon} me-2`}></i>
              {tab.label}
            </button>
          ))}
        </div>
      </nav>

      {/* Main Content */}
      <main className="dashboard-content">
        {renderTabContent()}
      </main>

      {/* Placement Form Modal */}
      {showForm && (
        <PlacementForm
          placement={selectedPlacement}
          onSave={selectedPlacement ? handleUpdatePlacement : handleCreatePlacement}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
};

export default PlacementDashboard;
